import {Badge, Box, HStack, Image, Text} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {useTranslation} from 'react-i18next';
import {useHalState, useHalStates} from '@synako/haligator';
import {IconeTemperatureLivraison} from '@/components/IconeTemperatureLivraison';
import {
  dateEnTexte,
  toDecimalFr,
  useAffichagePrixTtc,
  useLivraisonsPossiblesContext,
  usePanier,
  usePanierCommands,
} from 'ff-common';
import {ShippingCartIcon} from '../../svg/ShippingCartIcon';
import {useCapture} from '@/CaptureSetup';
import {ChangerDateDeLivraisonModale} from '@/components/Modale/CalendarModale/ChangerDateDeLivraisonModale';
import {ListePaniersModale} from '@/components/Modale/ListePaniersModale';
import {VStack} from '@chakra-ui/layout';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useNavigatePanier} from '@/hooks/panier/useNavigatePanier';
import {
  CatalogueMarketplace,
  ChangerDateDeLivraisonCommand,
} from 'api-types/ffconnect';
import {useClient} from '@/haligator-factory';
import {SIDE_PANEL_WIDTH} from '@/components/SidePanelPanier/SidePanelPanier';
import {CartIconWithoutPlus} from '@/components/svg/CartIconWithoutPlus';
import {getModeTheme} from '@/utils/appMode';
import {useBasculerSidePanelPanier} from '@/hooks/panier/useBasculerSidePanelPanier';

export const CartButton: React.FC = () => {
  const capture = useCapture();
  const {selectedMarketplaceId} = useLivraisonsPossiblesContext();

  return (
    <HStack
      w={{base: '350px', md: SIDE_PANEL_WIDTH}}
      height='100%'
      px={2}
      // flex='1'
      // alignItems='stretch'
      justifyContent='center'
      // gap={{base: '1', md: '2'}}
      // px={{base: '2', md: '2', lg: '0'}}
      py={{base: '1', md: '0'}}
    >
      {selectedMarketplaceId ? (
        <MarketplaceCartSection
          marketplaceId={selectedMarketplaceId}
          capture={capture}
        />
      ) : (
        <>
          <DateLivraisonButton />
          <FFCartSection capture={capture} />
        </>
      )}
    </HStack>
  );
};

// Bouton montant panier avec badge nombre de lignes
// Desktop : ouvre le side panel panier
// Mobile : navigue vers la page panier
const CartSection: React.FC<{
  capture: ReturnType<typeof useCapture>;
  nombre_de_lignes: number;
  total_estime_ht: number;
  total_estime_ttc: number; // Affiché à la place du HT quand l'affichage des prix TTC est activé pour ce client
}> = ({nombre_de_lignes, total_estime_ht, total_estime_ttc}) => {
  // Pilotage de l'ouverture du side panel panier
  const basculerSidePanel = useBasculerSidePanelPanier();
  const {modeColor} = getModeTheme();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());
  const totalAffiche = affichagePrixTtc ? total_estime_ttc : total_estime_ht;

  // Toggle side panel (mobile et desktop)
  return (
    <VStack
      onClick={() => {
        basculerSidePanel();
      }}
      bg={modeColor('blue.main', 'red.assistance')}
      borderRadius='10px'
      alignItems='center'
      justifyContent='center'
      px={{base: '1', md: '4'}}
      height='100%'
      gap='2'
      cursor='pointer'
      _hover={{bg: modeColor('blue.dark', 'red.assistance_prix')}}
      transition='all 0.2s ease'
      flexDirection='row'
    >
      <Box position='relative'>
        <CartIconWithoutPlus
          color='white'
          width='20px'
          height='20px'
        />
        {nombre_de_lignes > 0 && (
          <Badge
            position='absolute'
            top='0'
            right='-1'
            borderRadius='full'
            bg='white'
            color={modeColor('blue.main', 'red.assistance')}
            fontSize='0.6em'
            fontWeight='700'
            transform='translate(25%, -25%)'
            boxSize={4}
            textAlign='center'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            {nombre_de_lignes}
          </Badge>
        )}
      </Box>
      <Text
        color='white'
        fontWeight='700'
        fontSize={{base: 'xs', md: 'sm'}}
        whiteSpace='nowrap'
      >
        {toDecimalFr(totalAffiche)}&nbsp;€
      </Text>
    </VStack>
  );
};

// Cart Icon + total
const FFCartSection: React.FC<{capture: ReturnType<typeof useCapture>}> = ({
  capture,
}) => {
  const {data: panierData} = useHalState(usePanier());

  return (
    <CartSection
      capture={capture}
      nombre_de_lignes={panierData?.nombre_de_lignes ?? 0}
      total_estime_ht={panierData?.total_estime_ht ?? 0}
      total_estime_ttc={panierData?.total_estime_ttc ?? 0}
    />
  );
};

const MarketplaceCartSection: React.FC<{
  marketplaceId: CatalogueMarketplace;
  capture: ReturnType<typeof useCapture>;
}> = ({marketplaceId, capture}) => {
  const {t} = useTranslation('common');
  const client = useClient();
  const {modeColor} = getModeTheme();

  const {data: marketplaceData} = useHalState(
    client.follow('go:marketplace', {marketplace_id: marketplaceId}),
  );
  const logoSrc = marketplaceData?.logo_horizontal_couleur
    ? `${import.meta.env['VITE_MEDIA_URL']}${marketplaceData.logo_horizontal_couleur}`
    : undefined;

  const paniersHook = useHalStates(
    client
      .follow('paniers', {type_panier: marketplaceId, statut: 'ouvert'})
      .followAll('items'),
  );

  if (
    !paniersHook.loading &&
    (!paniersHook.states || paniersHook.states.length === 0)
  ) {
    return (
      <>
        <DateLivraisonButton />
        <FFCartSection capture={capture} />
      </>
    );
  }

  const panierState = paniersHook.states?.[0];
  const nombre_de_lignes = panierState?.data.nombre_de_lignes ?? 0;
  const total_estime_ht = panierState?.data.total_estime_ht ?? 0;
  const total_estime_ttc = panierState?.data.total_estime_ttc ?? 0;

  return (
    <>
      {/* Marketplace : même style que DateLivraisonButton avec logo */}
      <HStack
        bg={modeColor('blue.bg', 'red.assistance_bg')}
        borderRadius='10px'
        px='2'
        height='100%'
        alignItems='center'
        onClick={() => {
          NiceModal.show(ListePaniersModale);
        }}
        gap='2'
        cursor='pointer'
        _hover={{bg: modeColor('lblue.300', 'red.assistance_bg')}}
        transition='all 0.2s ease'
      >
        <ShippingCartIcon
          color={modeColor('blue.main', 'red.assistance')}
          width='24px'
          height='24px'
        />
        <VStack
          gap='0'
          alignItems='start'
        >
          <HStack
            gap='2'
            alignItems='center'
          >
            <Text
              fontSize='2xs'
              fontWeight='500'
              color='gray.dark'
              whiteSpace='nowrap'
            >
              {t('panier.livraison_selectionnee')}
            </Text>
            {logoSrc ? (
              <Image
                src={logoSrc}
                alt={marketplaceData?.nom ?? marketplaceId}
                h='14px'
                objectFit='contain'
              />
            ) : (
              <Text
                color={modeColor('blue.main', 'red.assistance')}
                whiteSpace='nowrap'
                fontWeight='700'
                fontSize='2xs'
              >
                {marketplaceData?.nom ?? marketplaceId}
              </Text>
            )}
          </HStack>
          <HStack
            gap='2'
            alignItems='center'
          >
            <Text
              color={modeColor('blue.main', 'red.assistance')}
              whiteSpace='nowrap'
              fontWeight='700'
              fontSize='xs'
            >
              {t('panier.livraison_48h_72h_court')}
            </Text>
            <Text
              color='black.text'
              textDecoration='underline'
              fontSize='xs'
              whiteSpace='nowrap'
            >
              {t('global.modifier')}
            </Text>
          </HStack>
        </VStack>
      </HStack>

      {/* Cart icon + total */}
      <CartSection
        capture={capture}
        nombre_de_lignes={nombre_de_lignes}
        total_estime_ht={total_estime_ht}
        total_estime_ttc={total_estime_ttc}
      />
    </>
  );
};

const DateLivraisonButton: React.FC = () => {
  const {date_de_livraison, effectiveTemperature} =
    useLivraisonsPossiblesContext();
  const {t} = useTranslation('common');
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const navigatePanier = useNavigatePanier();
  const panier = usePanier();
  const panierHook = useHalState(panier);
  const panierWithCommands = usePanierCommands(
    panier,
    textToast,
    onError,
    navigatePanier,
  );
  const {modeColor} = getModeTheme();

  const handlePress = () => {
    // Ouvre modale de changer la date de livraison du panier en cours, puis on change la date de livraison globale
    // Si pas de panier en cours, on change la date de livraison globale
    NiceModal.show(ChangerDateDeLivraisonModale, {
      redirectionAuPanier: false,
      changerDateDeLivraison: true,
      submit: async (event: ChangerDateDeLivraisonCommand) => {
        // Change la date du panier seulement si un panier existe
        if (panierHook.data) {
          return panierWithCommands.changerDateDeLivraison.submitAsync({
            date_de_livraison: event.date_de_livraison,
          });
        }
        return undefined;
      },
    });
  };

  return (
    <HStack
      bg={modeColor('blue.bg', 'red.assistance_bg')}
      borderRadius='10px'
      px='2'
      height='100%'
      alignItems='center'
      onClick={handlePress}
      gap='2'
      cursor='pointer'
      _hover={{bg: modeColor('lblue.300', 'red.assistance_bg')}}
      transition='all 0.2s ease'
      justifyContent='space-between'
      w='100%'
    >
      <HStack>
        <IconeTemperatureLivraison
          temperature_tournee={effectiveTemperature}
          boxSize={6}
          fallback={
            <ShippingCartIcon
              color={modeColor('blue.main', 'red.assistance')}
              width='24px'
              height='24px'
            />
          }
        />
        <VStack
          gap='0'
          alignItems='start'
        >
          <Text
            fontSize='2xs'
            fontWeight='500'
            color='gray.dark'
            whiteSpace='nowrap'
          >
            {t('panier.livraison_selectionnee')}
          </Text>
          <HStack
            gap='2'
            alignItems='center'
          >
            <Text
              color={modeColor('blue.main', 'red.assistance')}
              whiteSpace='nowrap'
              fontWeight='700'
              fontSize='xs'
            >
              {dateEnTexte(date_de_livraison)}
            </Text>
            <Text
              color='black.text'
              textDecoration='underline'
              fontSize='xs'
              whiteSpace='nowrap'
            >
              {t('global.modifier')}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </HStack>
  );
};
